import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-emerald-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-emerald-300 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-emerald-400" />
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-emerald-200">Last updated: January 30, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              By accessing or using LedgerAMT ("the Service"), operated by Africa Market for Trade ("we," "us," or "our"), 
              you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-600 leading-relaxed">
              LedgerAMT is a software-as-a-service (SaaS) platform that provides document management, invoicing, 
              receipts, quotes, and financial reporting tools for businesses. The Service is provided on a subscription basis.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Account Registration</h2>
            <div className="space-y-3 text-gray-600">
              <p>To use the Service, you must:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Be at least 18 years old or have legal authority to enter contracts</li>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be responsible for all activities under your account</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">4. Subscription and Payment</h2>
            <div className="space-y-3 text-gray-600">
              <p><strong>4.1 Subscription:</strong> Access to LedgerAMT requires an active paid subscription. Payment is required upfront.</p>
              <p><strong>4.2 Billing:</strong> Subscriptions are billed monthly or annually based on your selected plan.</p>
              <p><strong>4.3 Payment Processing:</strong> Payments are processed securely through Stripe. We do not store your payment card details.</p>
              <p><strong>4.4 Renewal:</strong> Subscriptions automatically renew unless cancelled before the renewal date.</p>
              <p><strong>4.5 Refunds:</strong> We offer a 30-day money-back guarantee for annual subscriptions. Monthly subscriptions are non-refundable.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
            <p className="text-gray-600 mb-4">You agree not to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Use the Service for any illegal purpose</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the intellectual property rights of others</li>
              <li>Transmit malware, viruses, or harmful code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use the Service to send spam or unsolicited communications</li>
              <li>Resell or redistribute the Service without authorization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Your Data</h2>
            <div className="space-y-3 text-gray-600">
              <p><strong>6.1 Ownership:</strong> You retain all rights to the data you upload to the Service.</p>
              <p><strong>6.2 License:</strong> You grant us a limited license to use your data solely to provide and improve the Service.</p>
              <p><strong>6.3 Backup:</strong> While we maintain regular backups, you are responsible for maintaining your own backup copies of important data.</p>
              <p><strong>6.4 Deletion:</strong> Upon account termination, we will delete your data within 30 days, unless legally required to retain it.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Team Accounts</h2>
            <div className="space-y-3 text-gray-600">
              <p><strong>7.1 Admin Responsibilities:</strong> Account administrators are responsible for all team member activities.</p>
              <p><strong>7.2 Member Limit:</strong> Each subscription allows up to 3 team members at no additional cost.</p>
              <p><strong>7.3 Permissions:</strong> Administrators control team member access and permissions within the Service.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              The Service, including its original content, features, and functionality, is owned by Africa Market for Trade 
              and is protected by international copyright, trademark, and other intellectual property laws. 
              LedgerAMT is a trademark of Africa Market for Trade.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-gray-600 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. 
              WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. 
              YOU USE THE SERVICE AT YOUR OWN RISK.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, AFRICA MARKET FOR TRADE SHALL NOT BE LIABLE FOR ANY 
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, 
              DATA, OR BUSINESS OPPORTUNITIES, REGARDLESS OF THE CAUSE OF ACTION.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
            <p className="text-gray-600 leading-relaxed">
              You agree to indemnify and hold harmless Africa Market for Trade from any claims, damages, losses, 
              or expenses arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">12. Termination</h2>
            <div className="space-y-3 text-gray-600">
              <p><strong>12.1 By You:</strong> You may cancel your subscription at any time through your account settings.</p>
              <p><strong>12.2 By Us:</strong> We may suspend or terminate your account for violation of these Terms or non-payment.</p>
              <p><strong>12.3 Effect:</strong> Upon termination, your right to use the Service ceases immediately.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">13. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the State of Indiana, 
              United States, and the Republic of Liberia, without regard to conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will provide notice of material changes 
              via email or through the Service. Continued use after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
            <div className="text-gray-600 space-y-2">
              <p>For questions about these Terms, please contact us:</p>
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <p className="font-semibold text-gray-800">Africa Market for Trade</p>
                <p>Email: hello@africamarket.co</p>
                <p>Email: hello@ledgeramt.com</p>
                <p>Phone: +1 929-355-2649 | +1 202-492-2533 | +231777527159</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium">Liberia Office</p>
                    <p>P.O. Box 283 Omega</p>
                    <p>Wehnzon Community</p>
                    <p>Paynesville, Liberia</p>
                  </div>
                  <div>
                    <p className="font-medium">United States Office</p>
                    <p>11 Municipal Drive</p>
                    <p>200PMB1027</p>
                    <p>Fishers, Indiana 46038, USA</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-emerald-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-emerald-200 text-sm">
            © {new Date().getFullYear()} Africa Market for Trade. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
